// Donut + legend pair used by the Composition section (Gender, Employee Type,
// Work Mode). Accepts arbitrary {name, value} rows and a colour palette.

import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';

import { formatNumberFull } from '../../../utils/FormatUtils';

export interface DonutDatum {
  name: string;
  value: number;
}

export interface DonutBreakdownProps {
  data: DonutDatum[];
  colors: string[];
  /** Called when a slice or legend row is clicked. */
  onSlice?: (datum: DonutDatum) => void;
  /** Total to use for percentages; defaults to the sum of `data`. */
  total?: number;
  /** Height of the donut chart in pixels. */
  height?: number;
}

const DonutBreakdown: React.FC<DonutBreakdownProps> = ({
  data,
  colors,
  onSlice,
  total,
  height = 148,
}) => {
  const safeTotal = total ?? data.reduce((sum, d) => sum + d.value, 0);
  return (
    <Box>
      <Box sx={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              innerRadius={42}
              outerRadius={66}
              paddingAngle={2}
              dataKey="value"
              onClick={(d) => onSlice?.(d as DonutDatum)}
              style={{ cursor: onSlice ? 'pointer' : 'default' }}
            >
              {data.map((_, idx) => (
                <Cell key={idx} fill={colors[idx % colors.length]} />
              ))}
            </Pie>
            <RechartsTooltip
              formatter={(value: number, name: string) => [formatNumberFull(value), name]}
              contentStyle={{
                borderRadius: 8,
                border: '1px solid #E2E8F0',
                fontSize: 12,
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {data.map((d, idx) => {
          const pct = safeTotal > 0 ? (d.value / safeTotal) * 100 : 0;
          const row = (
            <Stack
              key={d.name}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 0.5,
                py: 0.5,
                borderRadius: 1,
                cursor: onSlice ? 'pointer' : 'default',
                '&:hover': onSlice ? { backgroundColor: '#F8FAFC' } : {},
              }}
              onClick={onSlice ? () => onSlice(d) : undefined}
            >
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    backgroundColor: colors[idx % colors.length],
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {d.name}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: 'text.primary',
                }}
              >
                {pct.toFixed(1)}%
              </Typography>
            </Stack>
          );
          return row;
        })}
      </Stack>
    </Box>
  );
};

export default DonutBreakdown;
